import { useEffect, useState, useRef } from "react";
import {
  BaSeGrid,
  BaSeGridItem,
  BaSeIcon,
  BaSeInput,
  BaSeOptionPicker,
} from "@base/react";
import { convertObjectValuesToFloat, formatCurrency, sortedArrayByObjectKeys, sumValuesPercent } from "../../../services/utils";
import { hasDuplicates } from "../../planning/components/functions";
import InputPercentage from "../../../components/form-builder/input-percentage";
import Button from "../../../components/button/button";

const PostCreditSection = ({ step, data = [], isDisabled, onChange, onSubmit, onInvalidChange, lab = false }) => {
  const [deadlines, setDeadlines] = useState([]);
  const [hasRepeats, setHasRepeats] = useState(false);
  const [invalidSum, setInvalidSum] = useState(false);
  const [sum, setSum] = useState(undefined);
  const isInitialMount = useRef(true);
  const prevInvalidSumRef = useRef(invalidSum);

  useEffect(() => {
    if (isInitialMount.current) {
      if (data.length) {
        const sorted = sortedArrayByObjectKeys(data);
        setDeadlines(sorted);
      } else {
        setDeadlines([{ order: 0, value: 0 }]);
      }
      isInitialMount.current = false;
    }
  }, [data, step]);

  useEffect(() => {
    const isFormValid = () => {
      for (const item of deadlines) {
        const stringValue = String(item.value ?? "");

        if (stringValue[0] === ',' || stringValue[0] === '.') return false;
        if (stringValue[stringValue.length - 1] === ',' || stringValue[stringValue.length - 1] === '.') return false;
        if (stringValue.includes(',') && stringValue.includes('.')) return false;
        if (/^[,.]+$/.test(stringValue)) return false;
      }

      return true;
    };

    const sumCheck = sumValuesPercent(convertObjectValuesToFloat(deadlines)) !== 100 || !isFormValid();

    if (prevInvalidSumRef.current !== sumCheck) {
      setInvalidSum(sumCheck);
      if (onInvalidChange) onInvalidChange(sumCheck);
      prevInvalidSumRef.current = sumCheck;
    }
  }, [deadlines, onInvalidChange]);

  const handleSubmit = () => {
    if (onSubmit) {
      onSubmit(deadlines);
    } else {
      onChange?.(null, deadlines);
    }
  };

  const getNextOrderValue = () => {
    const maxOption = 12;
    const selected = deadlines.map(d => Number(d.order));
    const used = Array(maxOption + 1).fill(false);

    selected.forEach(v => {
      if (v >= 0 && v <= maxOption) used[v] = true;
    });

    const highest = Math.max(...selected, -1);

    for (let i = highest + 1; i <= maxOption; i++) {
      if (!used[i]) return i;
    }

    for (let i = 0; i <= maxOption; i++) {
      if (!used[i]) return i;
    }

    return 0;
  };

  const addDeadline = () => {
    const nextValue = getNextOrderValue();

    const newDeadline = {
      order: Number(nextValue),
      value: 0
    };

    const updated = sortedArrayByObjectKeys([...deadlines, newDeadline]);
    setDeadlines(updated);
    setHasRepeats(hasDuplicates(updated.map(d => Number(d.order))));
  };

  const handleOrderChange = (currentOrder, selectedValues) => {
    const selectedId = selectedValues?.[0]?.id;
    const newOrder = Number(selectedId);

    const updated = deadlines.map(item => {
      if (item.order === currentOrder) {
        return { ...item, order: newOrder };
      }
      return item;
    });
    const sorted = sortedArrayByObjectKeys(updated);
    setDeadlines(sorted);
    setHasRepeats(hasDuplicates(sorted.map(d => Number(d.order))));
  };

  const handleValueChange = (order, newValue) => {
    const inputValue = typeof newValue === 'object' && newValue?.target 
      ? newValue.target.value 
      : newValue;
    
    const stringValue = inputValue.toString();
    
    if (stringValue === "") {
      const updated = deadlines.map(item =>
        item.order === order ? { ...item, value: 0 } : item
      );
      const sorted = sortedArrayByObjectKeys(updated);
      setDeadlines(sorted);
      return;
    }
    
    const allowedValue = stringValue.replace(/[^\d,.]/g, "");
    
    const normalizedForParsing = allowedValue.replace(',', '.');
    const numericValue = parseFloat(normalizedForParsing);
    
    if (!isNaN(numericValue) && numericValue > 100) {
      const updated = deadlines.map(item =>
        item.order === order ? { ...item, value: 100 } : item
      );
      const sorted = sortedArrayByObjectKeys(updated);
      setDeadlines(sorted);
      return;
    }

    const finalValue = !isNaN(numericValue) ? numericValue : 0;
    const updated = deadlines.map(item =>
      item.order === order ? { ...item, value: finalValue } : item
    );
    const sorted = sortedArrayByObjectKeys(updated);
    setDeadlines(sorted);
  };

  useEffect(() => {
    const currentSum = deadlines.reduce((acc, item) => {
      const normalized = String(item.value || 0).replace(',', '.');
      const num = parseFloat(normalized);
      return acc + (isNaN(num) ? 0 : num);
    }, 0);
    setSum(currentSum);
  }, [deadlines]);

  useEffect(() => {
    if (!isInitialMount.current && onChange && !isDisabled && sum !== undefined) {
      onChange(deadlines);
    }
  }, [sum, deadlines, onChange, isDisabled]);

  const getOrderOptions = (currentOrder) => {
    const selectedOrders = deadlines
      .filter(item => item.order !== currentOrder)
      .map(item => Number(item.order));

    return Array.from({ length: 13 })
      .map((_, index) => ({
        id: Number(index),
        value: Number(index),
        label: index === 0 ? "À vista" : index === 1 ? "30 dias" : `${index}x`
      }))
      .filter(option => !selectedOrders.includes(option.id));
  };

  const handleDaysChange = (days, newDays) => {
    const daysValue = Number(newDays.target?.value ?? newDays);
    
    const updated = deadlines.map(item => {
      if (item.days === days) {
        return { ...item, days: daysValue };
      }
      return item;
    });
    const sorted = sortedArrayByObjectKeys(updated);
    setDeadlines(sorted);
    setHasRepeats(hasDuplicates(sorted.map(d => Number(d.order))));
  };

  const deleteRow = (index) => {
    const updated = deadlines.filter((_, i) => i !== index);
    setDeadlines(updated);
    setHasRepeats(hasDuplicates(updated.map(d => Number(d.order))));
  }

  return (
    <div className="post-credit-section">
      <BaSeGrid>
        {deadlines.map((item, index) => (
          <BaSeGridItem key={item.order} desktop={{ size: 19 }}>
            <div className="deadline-row">
              <div className="deadline-select-wrapper">
                <label className="deadline-label">{step === 7 ? 'Dias' : `Prazo do ${step === 6 ? 'pagamento' : 'recebimento'}`}</label>
                <div className="deadlines__wrapper-inline">
                  {step === 7 ? (
                    <BaSeInput
                      value={item.days}
                      onChange={e => handleDaysChange(item.days, e)}
                      inputMode="decimal"
                      width={lab ? 300 : 170}
                      placeholder="0"
                      isDisabled={isDisabled}
                    />
                  ) : (
                    <BaSeOptionPicker
                      values={getOrderOptions(item.order)}
                      onChange={selectedValues => handleOrderChange(item.order, selectedValues)}
                      enableSelectAll={false}
                      maxOptionsSelected={1}
                      withFilterText={false}
                      emptyValueLabel="Selecione"
                      initialSelectedValuesId={
                        item.order !== undefined ? [Number(item.order)] : []
                      }
                      width={lab ? 300 : 216}
                      isDisabled={isDisabled}
                    />
                  )}
                </div>
              </div>

              <div className="deadline-value-wrapper">
                <BaSeInput
                  name={`post_credit_value_${item.order}`}
                  label={step === 7 ? '% das compras' : 'Valor'}
                  placeholder="0%"
                  value={item.value}
                  onChange={e => handleValueChange(item.order, e.target?.value ?? e)}
                  isDisabled={isDisabled}
                  maxLength={6}
                  suffix="%"
                  width={lab ? 300 : 170}
                />
              </div>

              {!isDisabled && (
                <div className="icon-wrapper">
                  <BaSeIcon
                    name="trash-alt"
                    description="Excluir linha"
                    size={16}
                    color="#243342"
                    onClick={() => deleteRow(index)}
                  />
                </div>
              )}
            </div>
          </BaSeGridItem>
        ))}
      </BaSeGrid>

      <div
        className={`base-input-container ${
          sumValuesPercent(convertObjectValuesToFloat(deadlines)) <= 100
            ? "input-100"
            : "input-not-100"
        }`}
      >
        <InputPercentage
          suffix="%"
          value={formatCurrency(sumValuesPercent(convertObjectValuesToFloat(deadlines)))}
          placeholder="%"
          width="205px"
          isDisabled={true}
        />
      </div>

      {!isDisabled && (
        <button
          type="button"
          className="btn-without-style btn-add-more"
          onClick={addDeadline}
          disabled={hasRepeats}
        >
          <BaSeIcon
            color="#2E5DE6"
            description="Adicionar"
            name="plus-square"
            size={24}
          />
          <span>Adicionar prazo e percentual</span>
        </button>
      )}
      <div className="sum-warning">
        <span>Atenção! A soma dos percentuais lançados{" "}<strong>DEVE SER IGUAL a 100%</strong>.</span>
      </div>
      <div className="send-button">
        {!lab && <Button
          disabled={invalidSum || isDisabled}
          callback={handleSubmit}
        >
          Atualizar
        </Button>}
      </div>
    </div>
  );
};

export default PostCreditSection;
